import { useInfiniteQuery } from '@tanstack/react-query';

import { useMemo } from 'react';

import type { ConversationListRow, PaginatedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { conversationsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

const CONVERSATIONS_PAGE_LIMIT = 20;

export interface SearchConversationsParams {
	/** Free-text search query. */
	query?: string;
	/** Maximum number of results per page. */
	limit?: number;
	/** Facet fields to include in the response. */
	facets?: string[];
	/** Filter criteria for narrowing results. */
	filters?: {
		leadId?: string;
		channels?: string | string[];
		status?: string | string[];
	};
}

export const searchConversations = async (
	servicesProviderId: string,
	params: SearchConversationsParams = {},
	page: number = 0,
): Promise<PaginatedResponse<ConversationListRow>> => {
	const response = await apiClient.post<PaginatedResponse<ConversationListRow>>(
		conversationsEndpoints.search(servicesProviderId),
		{ ...params, page, limit: params.limit ?? CONVERSATIONS_PAGE_LIMIT },
	);
	return response.data;
};

export interface SearchConversationsResult {
	/** Flattened list of all fetched conversations across all pages. */
	conversations: ConversationListRow[];
	/** Whether the initial page is still loading. */
	isLoading: boolean;
	/** Whether the most recent fetch encountered an error. */
	isError: boolean;
	/** Refetch conversations from the beginning. */
	refetch: () => void;
	/** Whether more pages are available. */
	hasNextPage: boolean;
	/** Whether the next page is currently being fetched. */
	isFetchingNextPage: boolean;
	/** Fetch the next page of conversations. */
	fetchNextPage: () => void;
}

export interface SearchConversationsOptions {
	/** When false, disables the query entirely. Defaults to true. */
	enabled?: boolean;
}

export const useSearchConversations = (
	params: SearchConversationsParams = {},
	options?: SearchConversationsOptions,
): SearchConversationsResult => {
	const servicesProviderId = useCurrentServicesProviderId();

	const query = useInfiniteQuery({
		queryKey: queries.conversations.list(servicesProviderId, params).queryKey,
		queryFn: ({ pageParam }) => searchConversations(servicesProviderId, params, pageParam),
		initialPageParam: 0,
		getNextPageParam: (lastPage, _allPages, lastPageParam) =>
			lastPage.hasMore ? lastPageParam + 1 : undefined,
		enabled: Boolean(servicesProviderId) && (options?.enabled ?? true),
		staleTime: 30_000,
		refetchInterval: params.filters?.leadId ? undefined : 30_000,
		throwOnError: false,
	});

	const conversations = useMemo<ConversationListRow[]>(() => {
		if (!query.data?.pages) return [];
		return query.data.pages.flatMap((page) => page.data);
	}, [query.data?.pages]);

	return {
		conversations,
		isLoading: query.isLoading,
		isError: query.isError,
		refetch: query.refetch,
		hasNextPage: query.hasNextPage ?? false,
		isFetchingNextPage: query.isFetchingNextPage,
		fetchNextPage: query.fetchNextPage,
	};
};
